import React, { createContext, useReducer, useEffect } from 'react';
import {
  getDefaultSearchParams,
  getParamsFromQuerystring,
  updateOrSetQuerystringFromParams,
} from '../searchHelpers';
import { Trans } from '@fec/assets/js/utils/trans';

const initialState = {
  searchParams: {},
  searchConfig: {},
  translations: [],
};
const searchContextStore = createContext(initialState);
const { Provider } = searchContextStore;

export const ACTION_UPDATE_SEARCH_PARAMS = 'action.update.search.params';
export const ACTION_SYNC_SEARCH_PARAMS = 'action.sync.search.params';
export const ACTION_NEXT_PAGE = 'action.next.page';
export const ACTION_RESET_QUERY = 'action.reset.query';

const SearchContextProvider = ({
  searchConfig,
  children,
  translationSource,
}) => {
  // Collect default params and url params defined by config
  const initializer = () => {
    const urlParams = getParamsFromQuerystring();
    const urlParamsWithDefaults = {
      ...getDefaultSearchParams(true, searchConfig.filters),
      ...urlParams,
    };

    const translations = new Trans(translationSource).getAll();

    return {
      searchParams: urlParamsWithDefaults,
      searchConfig: searchConfig,
      translations: translations,
    };
  };

  const fromQueryParams = () => {
    const urlParams = getParamsFromQuerystring();
    const urlParamsWithDefaults = {
      ...getDefaultSearchParams(true, searchConfig.filters),
      ...urlParams,
    };
    dispatch({
      type: ACTION_SYNC_SEARCH_PARAMS,
      payload: urlParamsWithDefaults,
    });
  };

  const resetQueryParam = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    urlParams.set('q', '');
    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${urlParams.toString()}`,
    );
  };

  const [state, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case ACTION_UPDATE_SEARCH_PARAMS:
          return Object.assign({}, state, { searchParams: action.payload });
        case ACTION_SYNC_SEARCH_PARAMS:
          return Object.assign({}, state, { searchParams: action.payload });
        case ACTION_NEXT_PAGE: {
          const nextPage = state.searchParams.page * 1 + 1;
          const newSearchParams = Object.assign({}, state.searchParams, {
            page: nextPage,
          });
          return Object.assign({}, state, { searchParams: newSearchParams });
        }
        case ACTION_RESET_QUERY: {
          const emptyQueryParams = Object.assign({}, state.searchParams, {
            q: '',
          });
          resetQueryParam();
          return Object.assign({}, state, { searchParams: emptyQueryParams });
        }
        default:
          throw new Error(
            'Action ' +
              action.type +
              ' is not handled within SearchContextProvider',
          );
      }
    },
    initialState,
    initializer,
  );

  useEffect(() => {
    window.addEventListener(
      'popstate',
      (event) => {
        if (event.state) {
          fromQueryParams();
        }
      },
      false,
    );
  }, []);

  useEffect(() => {
    updateOrSetQuerystringFromParams(state.searchParams);
  }, [state.searchParams]);

  return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

export { searchContextStore, SearchContextProvider };
